import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { XMarkIcon } from '@heroicons/react/24/solid';
import AddOptionButton from './AddOptionButton';
import { camelToSentenceCase, isValidObjectId } from '../commonUtils/string.helper';

// Optimized debounce hook with cleanup
const useDebouncedValue = (value, delay = 150) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debounced;
};

const SelectInput = React.memo(({
  fieldKey,
  type = 'map',
  list = [],
  onChange,
  selectedValue = '',
  placeholder,
  error,
  disabled,
  loading = false,
  allowAddToList = false,
  handleAddToList,
  clearable = false,
  className,
}) => {
  
  // Memoize expensive calculations
  const isCustomEntry = useMemo(() => 
    type === 'map' &&
    typeof selectedValue === 'string' &&
    selectedValue &&
    !isValidObjectId(selectedValue) &&
    !list.some((item) => item.value === selectedValue),
    [type, selectedValue, list]
  );

  const [isInputMode, setIsInputMode] = useState(isCustomEntry);
  const [searchTerm, setSearchTerm] = useState(isCustomEntry ? selectedValue : '');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 150);

  const inputRef = useRef(null);
  const searchInputRef = useRef(null);

  // FIX 1: Reset internal state when selectedValue is cleared/reset
  useEffect(() => {
    if (!selectedValue || selectedValue === '' || selectedValue === '0') {
      setIsInputMode(false);
      setSearchTerm('');
    } else if (isCustomEntry) {
      setIsInputMode(true);
      setSearchTerm(selectedValue);
    }
  }, [selectedValue, isCustomEntry]);

  useEffect(() => {
    if (inputRef.current && isInputMode) {
      inputRef.current.focus();
    }
  }, [isInputMode]);

  useEffect(() => {
    if (!isInputMode) {
      setSearchTerm('');
    }
  }, [isInputMode]);

  // Memoize handlers to prevent recreation
  const handleOnChange = useCallback((value) => {
    if (type === 'list') {
      onChange(value);
    } else if (type === 'map') {
      const selectedItem = list.find((item) => item.value === value);
      if (selectedItem) {
        onChange(selectedItem.value);
      }
    }
  }, [type, list, onChange]);

  const handleClear = useCallback((e) => {
    e.stopPropagation();
    setIsInputMode(false);
    setSearchTerm('');
    onChange('');
  }, [onChange]);

  const handleSearchInputClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleSearchInputChange = useCallback((e) => {
    e.stopPropagation();
    setSearchTerm(e.target.value);
  }, []);

  const handleInputModeToggle = useCallback(() => {
    setIsInputMode(false);
    setSearchTerm('');
    onChange('');
  }, [onChange]);

  const handleAddOptionClick = useCallback(() => {
    setIsInputMode(true);
    setSearchTerm('');
  }, []);

  // Optimize filtering with early returns and case-insensitive comparison
  const filteredList = useMemo(() => {
    if (!Array.isArray(list) || list.length === 0) return [];
    if (!debouncedSearchTerm.trim()) return list;
    
    const searchLower = debouncedSearchTerm.toLowerCase();
    
    return list.filter((item) => {
      if (type === 'list') {
        return typeof item === 'string' && item.toLowerCase().includes(searchLower);
      } else {
        return item?.label && item.label.toLowerCase().includes(searchLower);
      }
    });
  }, [debouncedSearchTerm, list, type]);

  // Memoize the input change handler for add mode
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onChange(value);
  }, [onChange]);

  // Early return for input mode to reduce render complexity
  if (allowAddToList && isInputMode) {
    return (
      <div className="flex items-center">
        <Input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          placeholder="Enter value..."
          className="w-full p-2 border rounded"
          disabled={disabled}
          ref={inputRef}
        />
        <button
          type="button"
          onClick={handleInputModeToggle}
          className="ml-2 p-2 bg-gray-200 hover:bg-gray-300 rounded"
          disabled={disabled}
        >
          <ChevronDownIcon className="w-5 h-5 text-gray-600" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <Select onValueChange={handleOnChange} value={selectedValue || ''} disabled={disabled}>
        <div className="relative">
          <SelectTrigger
            className={`w-full ${className || ''} ${error ? 'border-red-500 ring-red-500' : 'border-gray-300'} ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>

          {selectedValue && clearable && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-8 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full z-10"
              aria-label="Clear selection"
            >
              <XMarkIcon className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
        <SelectContent className="overflow-visible">
          <SelectGroup>
            <div className="relative">
              <div className="sticky top-0 z-10 bg-white pt-1 pb-2 px-1">
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                  onClick={handleSearchInputClick}
                  className="w-full p-2 border rounded mb-2"
                  placeholder="Search..."
                  disabled={disabled}
                  ref={searchInputRef}
                  onKeyDown={(e) => e.stopPropagation()}
                />
                {allowAddToList && (
                  <AddOptionButton
                    content={`Add New ${camelToSentenceCase(fieldKey)}`}
                    onClick={handleAddOptionClick}
                  />
                )}
              </div>
              <div className="max-h-60 overflow-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-4">
                    <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    <span className="ml-2 text-sm text-gray-400">Loading...</span>
                  </div>
                ) : (
                  <>
                    {isCustomEntry && !list.some((item) => item.value === selectedValue) && (
                      <SelectItem key="custom-entry" value={selectedValue}>
                        {selectedValue}
                      </SelectItem>
                    )}

                    {filteredList.length > 0 ? (
                      filteredList.map((item, i) => {
                        const key = `${item?.value || item}_${i}`;
                        const value = item?.value || item;
                        const displayText = type === 'list' ? item : item?.label || '';

                        return (
                          <SelectItem
                            key={key}
                            value={value}
                            disabled={disabled}
                          >
                            {displayText}
                          </SelectItem>
                        );
                      })
                    ) : (
                      <div className="text-muted-foreground text-center py-2">No match found</div>
                    )}
                  </>
                )}
              </div>
            </div>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
});

SelectInput.displayName = 'SelectInput';

export default SelectInput;